import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { HERO_SLIDES, HERO_FEATURES } from "../../data/site";
import { Container } from "./ui";

const AUTO_MS = 6000;

export default function HeroCarousel() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const n = HERO_SLIDES.length;

  const go = useCallback((d) => setIdx((i) => (i + d + n) % n), [n]);
  const to = (i) => setIdx(i);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % n), AUTO_MS);
    return () => clearInterval(t);
  }, [paused, n]);

  const slide = HERO_SLIDES[idx];

  // Touch swipe (mobile)
  const [touchX, setTouchX] = useState(null);
  const onTouchStart = (e) => setTouchX(e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchX === null) return;
    const dx = e.changedTouches[0].clientX - touchX;
    if (Math.abs(dx) > 45) go(dx < 0 ? 1 : -1);
    setTouchX(null);
  };

  return (
    <section className="relative pt-[72px]" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-testid="hero-carousel">
      <div className="relative h-[560px] w-full overflow-hidden md:h-[620px]" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        {/* slides */}
        <AnimatePresence mode="sync">
          <motion.div
            key={slide.id}
            initial={{ opacity: 0, scale: 1.06 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-0"
          >
            <img src={slide.img} alt={slide.alt} className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/30" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          </motion.div>
        </AnimatePresence>

        {/* text */}
        <div className="absolute inset-0 flex items-center">
          <Container>
            <AnimatePresence mode="wait">
              <motion.div
                key={slide.id + "-text"}
                initial={{ opacity: 0, y: 26 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -14 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="max-w-xl"
              >
                <span className="text-xs font-bold uppercase tracking-[0.25em] text-gold-500" data-testid="hero-eyebrow">{slide.eyebrow}</span>
                <h1 className="mt-4 font-display text-5xl font-bold leading-[0.98] tracking-tight text-white sm:text-6xl lg:text-7xl">
                  {slide.pre} <span className="text-gold-500">{slide.gold}</span>
                </h1>
                <p className="mt-5 max-w-md text-lg text-zinc-300">{slide.sub}</p>
                <div className="mt-7 flex flex-wrap items-center gap-4">
                  <Link to="/get-started" className="inline-flex items-center gap-2 rounded-full bg-gold-500 px-7 py-3.5 font-semibold text-black transition hover:bg-gold-400" data-testid="hero-get-website">
                    Get My Website <ArrowRight size={16} />
                  </Link>
                  <span className="inline-flex items-center gap-2 rounded-full border border-gold-500/40 bg-black/40 px-4 py-2 text-sm text-zinc-200">
                    Websites <span className="font-display text-lg font-bold text-gold-500">$999</span>
                  </span>
                </div>
              </motion.div>
            </AnimatePresence>
          </Container>
        </div>

        {/* arrows */}
        <button onClick={() => go(-1)} aria-label="Previous" data-testid="hero-prev"
          className="absolute left-3 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full border border-white/20 bg-black/40 text-white backdrop-blur transition hover:border-gold-500 hover:text-gold-400 md:left-6">
          <ChevronLeft size={20} />
        </button>
        <button onClick={() => go(1)} aria-label="Next" data-testid="hero-next"
          className="absolute right-3 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full border border-white/20 bg-black/40 text-white backdrop-blur transition hover:border-gold-500 hover:text-gold-400 md:right-6">
          <ChevronRight size={20} />
        </button>

        {/* dots */}
        <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 gap-2.5" data-testid="hero-dots">
          {HERO_SLIDES.map((s, i) => (
            <button key={s.id} onClick={() => to(i)} aria-label={`Slide ${i + 1}`} data-testid={`hero-dot-${i}`}
              className={`h-2.5 rounded-full transition-all ${i === idx ? "w-7 bg-gold-500" : "w-2.5 bg-white/40 hover:bg-white/70"}`} />
          ))}
        </div>
      </div>

      {/* feature strip */}
      <div className="border-y border-white/10 bg-[#070707]">
        <Container className="flex flex-col items-center gap-6 py-5 lg:flex-row lg:justify-between">
          <div className="grid w-full grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-4 lg:w-auto lg:flex lg:gap-8">
            {HERO_FEATURES.map((f) => (
              <div key={f.title} className="flex items-center gap-3">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-gold-500/40 text-gold-400"><f.icon size={17} /></span>
                <div className="leading-tight">
                  <div className="text-sm font-semibold text-white">{f.title}</div>
                  <div className="text-xs text-zinc-500">{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="hidden items-center gap-4 xl:flex">
            <span className="h-px w-10 bg-gold-500/60" />
            <p className="font-display text-sm font-bold uppercase tracking-[0.15em] text-gold-500">More than websites.<br />We build business growth.</p>
          </div>
        </Container>
      </div>
    </section>
  );
}
